import * as React from 'react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// ** MUI Components
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// ** Components
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import BlankLayout from '@erikdakoda/site-template-ui/layouts/BlankLayout';

// ** Hooks
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useRouter } from 'next/router';

// ** Utils
import { signOut } from '@erikdakoda/auth/client/betterAuthClient';
import authConfig from '@erikdakoda/auth/shared/config';
import { useState } from 'react';

const Error401Page = () => {
  'use memo';

  const { t } = useTranslation('auth-ui');
  const { user } = useCurrentUser();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = async () => {
    setIsLoading(true);
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          setIsLoading(false);
          router.push(authConfig.config.pages.login);
        },
        onError: () => {
          setIsLoading(false);
        },
      },
    });
  };

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      className="error-401-page"
    >
      <Stack
        direction="column"
        spacing={12}
        sx={{
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box>
          <Typography variant="h1">401</Typography>
          <Typography variant="h4" sx={{ marginBottom: 2 }}>
            {t('notAuthorized')}
          </Typography>
          <Typography variant="body2">{t('notAuthorizedDescription')}</Typography>
        </Box>
        <FaIconUvilo prefix="fat" icon="face-hand-peeking" size="4x" />
        <Button href="/" variant="contained">
          {t('backToHome')}
        </Button>
        <Box>
          <Typography variant="body1">
            {t('loggedInAs')} {user?.email}.
          </Typography>
          <Button onClick={handleLogout} variant="text" loading={isLoading}>
            {t('logOut')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

Error401Page.getLayout = (page: ReactNode) => <BlankLayout>{page}</BlankLayout>;
Error401Page.authGuard = false;
Error401Page.guestGuard = false;

export default Error401Page;
